import React, { useState } from "react";
import Breadcrumb from "../../../../Components/Utils/Breadcrumb";
import { BackButton } from "../../../../Components/Utils/Buttons";
import ModalContainer from "../../../../Components/Others/ModalContainer";
import axiosInstance from "../../../../Services/axiosCommon";
import { useTranslation } from "react-i18next";
import { useToast } from "../../../../Components/Others/ToastServices";
import {
  FiAlertCircle,
  FiClock,
  FiPlay,
  FiSettings,
  FiStopCircle,
} from "react-icons/fi";
import {
  TextMediumBase,
  TextMediumSM,
  TextMediumXS,
  TextMediumXXL,
} from "../../../../Components/Text/MediumText";

const MainSystemMaintenance = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const [minutes, setMinutes] = useState(20);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);

  const handleAction = async (action) => {
    setLoading(action);
    try {
      let response;
      if (action === "warning") {
        response = await axiosInstance.post("/auth/maintenance-warning", {
          minutes,
          message,
        });
      } else if (action === "start") {
        response = await axiosInstance.post("/auth/maintenance-start", {});
      } else if (action === "stop") {
        response = await axiosInstance.post("/auth/maintenance-stop", {});
      }

      if (response?.data.status) {
        toast.open({
          message: t("Action completed successfully"),
          description: response.data.message || t("Action completed successfully"),
          type: "success",
        });
      } else {
        toast.open({
          message: t("Action failed"),
          description: response?.data.message || t("Action failed"),
          type: "error",
        });
      }
    } catch (error) {
      toast.open({
        message: t("An error occurred"),
        description: error.response?.data?.message || t("An error occurred"),
        type: "error",
      });
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="w-full h-full px-4 py-2">
      <div className="w-full flex justify-between items-center">
        <Breadcrumb
          items={[
            {
              name: t("Settings"),
            },
            {
              name: t("System Maintenance"),
            },
          ]}
        />
        <BackButton />
      </div>

      <div className="w-full h-[calc(100vh-145px)] bg-white mt-3 mx-auto p-2 rounded-md overflow-y-auto">
        <div className="p-6 max-w-4xl mx-auto space-y-8">
          <div className="flex items-center space-x-3 border-b border-slate-200 pb-4">
            <FiSettings className="w-8 h-8 text-slate-600" />
            <div>
              <TextMediumXXL
                content={t("System Maintenance")}
                color="text-slate-800"
                otherStyles="font-bold"
              />
              <TextMediumSM
                content={t("Manage system-wide maintenance mode and warnings")}
                color="text-slate-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Set Warning Card */}
            <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6 space-y-4">
              <div className="flex items-center space-x-2 text-amber-600">
                <FiClock className="w-6 h-6" />
                <TextMediumBase
                  content={t("Set Warning (Countdown)")}
                  color="text-amber-600"
                  otherStyles="font-bold"
                />
              </div>
              <TextMediumXS
                content={t(
                  "Broadcast a warning message and a live countdown to all active users.",
                )}
                color="text-slate-500"
              />

              <div className="space-y-2">
                <div>
                  <label className="text-xs 2xl:text-sm font-medium text-textColor-500">
                    {t("Countdown Minutes")}
                  </label>
                  <input
                    type="number"
                    value={minutes}
                    onChange={(e) => setMinutes(parseInt(e.target.value))}
                    className="text-xs 2xl:text-sm font-medium border w-full h-10 px-3 rounded-[10px] outline-none bg-white border-slate-200 focus:border-amber-500 transition-all"
                    placeholder="20"
                  />
                </div>
                <div>
                  <label className="text-xs 2xl:text-sm font-medium text-textColor-500">
                    {t("Warning Message")}
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="text-xs 2xl:text-sm font-medium border w-full px-3 py-2 rounded-[10px] outline-none bg-white border-slate-200 focus:border-amber-500 transition-all h-24 resize-none"
                    placeholder={t(
                      "The system will be offline for a security update in 20 minutes.",
                    )}
                  />
                </div>
                <button
                  onClick={() => handleAction("warning")}
                  disabled={!!loading}
                  className="w-full py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-md font-bold transition-all disabled:opacity-50 text-xs 2xl:text-sm"
                >
                  {loading === "warning" ? t("Sending...") : t("Send Warning")}
                </button>
              </div>
            </div>

            <div className="space-y-6">
              {/* Start Maintenance Card */}
              <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6 space-y-4">
                <div className="flex items-center space-x-2 text-rose-600">
                  <FiAlertCircle className="w-6 h-6" />
                  <TextMediumBase
                    content={t("Start Maintenance")}
                    color="text-rose-600"
                    otherStyles="font-bold"
                  />
                </div>
                <TextMediumXS
                  content={t(
                    "Immediately log out all users and block access to the system. USE WITH CAUTION.",
                  )}
                  color="text-slate-500"
                />
                <button
                  onClick={() => setIsConfirmModalOpen(true)}
                  disabled={!!loading}
                  className="w-full py-2 bg-rose-500 hover:bg-rose-600 text-white rounded-md font-bold transition-all disabled:opacity-50 flex items-center justify-center space-x-2 text-xs 2xl:text-sm"
                >
                  <FiStopCircle className="w-5 h-5" />
                  <span>
                    {loading === "start"
                      ? t("Processing...")
                      : t("Activate Maintenance")}
                  </span>
                </button>
              </div>

              {/* Stop Maintenance Card */}
              <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6 space-y-4">
                <div className="flex items-center space-x-2 text-emerald-600">
                  <FiPlay className="w-6 h-6" />
                  <TextMediumBase
                    content={t("Stop Maintenance")}
                    color="text-emerald-600"
                    otherStyles="font-bold"
                  />
                </div>
                <TextMediumXS
                  content={t(
                    "Restore system access and allow users to log in again.",
                  )}
                  color="text-slate-500"
                />
                <button
                  onClick={() => handleAction("stop")}
                  disabled={!!loading}
                  className="w-full py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-md font-bold transition-all disabled:opacity-50 flex items-center justify-center space-x-2 text-xs 2xl:text-sm"
                >
                  <FiPlay className="w-5 h-5" />
                  <span>
                    {loading === "stop" ? t("Processing...") : t("Go Live")}
                  </span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ModalContainer
        visiable={isConfirmModalOpen}
        title={t("System Maintenance")}
        content={t("Did you set a warning for the users?")}
        handleClick={() => {
          setIsConfirmModalOpen(false);
          handleAction("start");
        }}
        closeModal={() => setIsConfirmModalOpen(false)}
        okText={t("Yes, Activate")}
        cancelText={t("Cancel")}
      />
    </div>
  );
};

export default MainSystemMaintenance;
